import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
} from "@heroicons/react/16/solid";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../api";
import { SegmentFiltersTooltip } from "../../../externalComponents/SegmentFiltersTooltip";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Button } from "../../../externalComponents/tailwind/button";
import EmptyState from "../../../externalComponents/tailwind/empty-state";
import { Link } from "../../../externalComponents/tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import { Text } from "../../../externalComponents/tailwind/text";

const PastSendings = forwardRef(({ user, isMobile }, ref) => {
  const navigate = useNavigate();
  const [pastBatches, setPastBatches] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const itemsPerPage = 5;

  const fetchPastBatches = async (resetPage = false) => {
    if (resetPage) {
      setCurrentPage(1);
    }
    try {
      const result = await api.view(
        `($customerId: String) {
          sendings {
            sendingBatchId
            bucket: count(1)
            [where customerId == $customerId && status == "completed" && (isTest == null || isTest == false) && campaign.automationType == null && sendingBatchId != null group by sendingBatchId]
          }
        }`,
        { customerId: user.customerId },
      );

      const batchIds = result?.sendings?.map((s) => s.sendingBatchId) || [];

      const enrichedBatches = await Promise.all(
        batchIds.map(async (batchId) => {
          let sendings = [];
          let tmp = await api.sending.findMany({
            first: 250,
            filter: {
              customerId: { equals: user.customerId },
              sendingBatchId: { equals: batchId },
              status: { equals: "completed" },
            },
            select: {
              id: true,
              sendingDate: true,
              contactList: true,
              campaign: { name: true, id: true },
              segment: { name: true },
              segmentFilters: true,
              contactListSegment: {
                externalId: true,
                type: true,
              },
            },
            sort: { sendingDate: "Ascending" },
          });

          sendings = sendings.concat(tmp);

          while (tmp.hasNextPage) {
            tmp = await tmp.nextPage();
            sendings = sendings.concat(tmp);
          }

          if (sendings.length === 0) {
            return null;
          }

          return {
            sendingBatchId: batchId,
            campaignId: sendings[0].campaign?.id,
            campaignName: sendings[0].campaign?.name || "-",
            segmentName: sendings[0].segment?.name || "-",
            segmentFilters: sendings[0].segmentFilters,
            contactListSegment: sendings[0].contactListSegment,
            sendingDate: new Date(sendings[0].sendingDate),
            endDate: new Date(sendings[sendings.length - 1].sendingDate),
            totalContacts: sendings.reduce(
              (sum, s) => sum + (s.contactList?.length || 0),
              0,
            ),
          };
        }),
      );

      const validBatches = enrichedBatches
        .filter((b) => b !== null)
        .sort((a, b) => b.sendingDate - a.sendingDate);

      setPastBatches(validBatches);
    } catch (error) {
      console.error("Error fetching past batches:", error);
      setPastBatches([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPastBatches(true);
    setRefreshing(false);
  };

  useImperativeHandle(ref, () => ({
    refresh: () => fetchPastBatches(true),
  }));

  useEffect(() => {
    if (user?.customerId) {
      fetchPastBatches();
    }
  }, [user?.customerId]);

  const totalPages = Math.ceil(pastBatches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBatches = pastBatches.slice(startIndex, endIndex);

  const getNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const rowMarkup = paginatedBatches.map((batch, index) => {
    return (
      <TableRow
        key={`batch-${batch.sendingBatchId}`}
        position={index}
        {...(!isMobile && {
          onClick: () => navigate(`/campaigns/edit/${batch.campaignId}`),
        })}
      >
        <TableCell className="font-bold">{batch.campaignName}</TableCell>
        <TableCell>
          <div>
            <div>
              {batch.sendingDate.toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              })}
            </div>
            {batch.endDate &&
              batch.endDate.getTime() !== batch.sendingDate.getTime() && (
                <div className="text-sm text-gray-500">
                  Ends:{" "}
                  {batch.endDate.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  })}
                </div>
              )}
          </div>
        </TableCell>
        <TableCell className="text-center">{batch.totalContacts}</TableCell>
        <TableCell className="w-[30%]">
          {batch.segmentFilters ? (
            <SegmentFiltersTooltip segmentFilters={batch.segmentFilters}>
              <span className="cursor-help">{batch.segmentName}</span>
            </SegmentFiltersTooltip>
          ) : batch.contactListSegment?.type === "klaviyo-segment" ? (
            <Link
              style={{
                wordBreak: "break-all",
                overflowWrap: "break-word",
              }}
              className="hover:underline"
              href={`https://www.klaviyo.com/list/${batch.contactListSegment?.externalId}`}
              target="_blank"
            >
              {`https://www.klaviyo.com/list/${batch.contactListSegment?.externalId}`}
            </Link>
          ) : (
            batch.segmentName
          )}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <>
      <br />
      <div className="flex items-center gap-3 mb-4">
        <Text className="text-xl font-bold">Past campaigns</Text>
        <Button
          color="secondaryButton"
          onClick={handleRefresh}
          disabled={refreshing}
          className="h-8 w-8 p-0 flex items-center justify-center"
          title="Refresh"
        >
          <ArrowPathIcon
            className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />
        </Button>
      </div>
      {pastBatches.length > 0 ? (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Campaign name</TableHeader>
                <TableHeader className="w-[30%]">Sending date</TableHeader>
                <TableHeader className="text-center">Recipients</TableHeader>
                <TableHeader className="w-[30%]">Segment</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>{rowMarkup}</TableBody>
          </Table>
          <div className="flex justify-start my-4">
            <Button
              color="secondaryButton"
              onClick={getPreviousPage}
              disabled={currentPage === 1}
            >
              <ArrowLeftIcon />
              Previous
            </Button>
            <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
              {currentPage}
            </Badge>
            <Button
              color="secondaryButton"
              onClick={getNextPage}
              disabled={currentPage >= totalPages}
            >
              Next
              <ArrowRightIcon />
            </Button>
          </div>
        </>
      ) : (
        <EmptyState heading="No message has been sent" />
      )}
    </>
  );
});

PastSendings.displayName = "PastSendings";

export default PastSendings;
